import { DEFAULT_ROWS, type DetectionRow } from './DetectionTable';
import { BLOG_POSTS, type BlogPost } from '../blog/posts';
import type { LegalPageSection } from './LegalPage';
import {
  LEGAL_LAST_UPDATED,
  TERMS_SECTIONS,
  PRIVACY_SECTIONS,
  REFUND_SECTIONS,
} from './legalContent';

/** Stable per-route identifier. The home + SEO routes use literal IDs;
 *  blog routes get auto-generated `blog-<slug>` IDs from the post data. */
export type RouteId = string;

export type RouteKind = 'app' | 'blog-index' | 'blog-post' | 'why' | 'legal';

export type SectionKind = 'text-pii' | 'exif' | 'faces' | 'pdf-meta';

export type Route = {
  id: RouteId;
  path: string;
  kind: RouteKind;
  /** <title> tag value for this route. */
  title: string;
  /** <meta name="description"> value. */
  description: string;
  /** H1 displayed in the hero (sans the trailing accent period). */
  heroH1: string;
  /** Hero lede paragraph copy. */
  heroLede: string;
  detectionRows?: DetectionRow[];
  /**
   * Sections allowed in the review panel for this route.
   * `undefined` means "all sections".
   */
  allowedSections?: ReadonlySet<SectionKind>;
  /** OpenGraph + Twitter card image URL (defaults to the site logo). */
  ogImage?: string;
  /** Set on `kind: 'blog-post'` routes — the underlying article data. */
  blogPost?: BlogPost;
  /** Set on `kind: 'legal'` routes — the hand-authored copy and the
   *  "last updated" stamp the page shows in its hero. */
  legal?: {
    lastUpdated: string;
    sections: readonly LegalPageSection[];
  };
};

const onlyRows = (...names: string[]): DetectionRow[] =>
  DEFAULT_ROWS.filter((r) => names.includes(r.name));

export const SITE_ORIGIN = 'https://anomia.app';

const HOME_H1 = "AI tools don't forget what you paste into them";
const HOME_LEDE =
  "drop a file here first. we strip names, addresses, and numbers — and the hidden metadata you can't see — all in your browser, before the file goes anywhere it can't come back from.";

const APP_ROUTES: Route[] = [
  {
    id: 'home',
    path: '/',
    kind: 'app',
    title: "anomia — clean files before AI tools see them",
    description:
      "AI tools don't forget what you paste into them. drop a file here first — we strip names, addresses, numbers, and hidden metadata, all in your browser, before the file goes anywhere it can't come back from.",
    heroH1: HOME_H1,
    heroLede: HOME_LEDE,
  },
  {
    id: 'strip-exif-from-photo',
    path: '/strip-exif-from-photo',
    kind: 'app',
    title: 'Strip EXIF data from photos — anomia',
    description:
      'Drop a JPEG or PNG and remove GPS, camera, and timestamp metadata before you share. 100% in-browser.',
    heroH1: 'Strip EXIF data from photos before you share them',
    heroLede:
      "Drop a photo and we'll show you every hidden field — GPS coordinates, camera model, timestamp — and remove the ones you don't want shipping with the image.",
    detectionRows: onlyRows('street addresses', 'IP addresses'),
    allowedSections: new Set<SectionKind>(['exif']),
  },
  {
    id: 'clean-pdf-metadata',
    path: '/clean-pdf-metadata',
    kind: 'app',
    title: 'Clean metadata out of PDF files — anomia',
    description:
      'Strip author, title, revision history, and creation timestamps from any PDF. Runs locally in your browser.',
    heroH1: 'Clean metadata out of PDF files',
    heroLede:
      "Author, title, software, revision history — they all ride along when you share a PDF. Drop yours and we'll show you what's there and let you wipe it.",
    allowedSections: new Set<SectionKind>(['pdf-meta']),
  },
  {
    id: 'blur-faces-in-photo',
    path: '/blur-faces-in-photo',
    kind: 'app',
    title: 'Blur faces in photos before you post — anomia',
    description:
      'Detect every face in a photo and apply a Gaussian blur. Runs locally with BlazeFace, never uploads.',
    heroH1: 'Blur faces in photos before you post them',
    heroLede:
      'Drop a photo. We find every face with a local face detector and let you confirm which to blur — no upload, no server, no exfiltration.',
    allowedSections: new Set<SectionKind>(['faces']),
  },
  {
    id: 'anonymize-filename',
    path: '/anonymize-filename',
    kind: 'app',
    title: 'Anonymize filenames before sharing — anomia',
    description:
      "Rename files to clean, generic names before you upload them. We don't keep your original filename.",
    heroH1: 'Anonymize filenames before you share',
    heroLede:
      "Filenames leak more than people think — names, dates, employer initials. Drop a file and we'll hand back a redacted version named simply 'redacted.{ext}'.",
    allowedSections: new Set<SectionKind>([]),
  },
];

const WHY_ROUTE: Route = {
  id: 'why',
  path: '/why',
  kind: 'why',
  title: 'the permanence problem — anomia',
  description:
    "why anomia exists, in long form. memorization, extraction, breaches, document leaks, and the asymmetric math that makes prevention free but recovery impossible. with citations.",
  heroH1: 'the permanence problem',
  heroLede: 'why anomia exists, in long form, with citations.',
};

const BLOG_INDEX_ROUTE: Route = {
  id: 'blog-index',
  path: '/blog',
  kind: 'blog-index',
  title: 'anomia blog — privacy, redaction, browser-only tools',
  description:
    "notes on PDF redaction, verifying privacy tools, and why we built anomia. new posts as we ship them.",
  heroH1: 'notes from the workshop',
  heroLede:
    "mostly technical: how the redaction works, how to verify a privacy tool isn't lying to you, and the occasional founder story.",
};

const LEGAL_ROUTES: Route[] = [
  {
    id: 'terms',
    path: '/terms',
    kind: 'legal',
    title: 'Terms of Service — anomia',
    description:
      'anomia terms of service. browser-only PII redaction tool. one-time $19 Pro license, 30-day no-questions refund, perpetual use.',
    heroH1: 'terms of service',
    heroLede: 'the rules for using anomia, in plain language.',
    legal: { lastUpdated: LEGAL_LAST_UPDATED, sections: TERMS_SECTIONS },
  },
  {
    id: 'privacy',
    path: '/privacy',
    kind: 'legal',
    title: 'Privacy Policy — anomia',
    description:
      "anomia's privacy policy. we don't collect or transmit the contents of your files — ever. what we do and don't see, in detail.",
    heroH1: 'privacy policy',
    heroLede:
      "we don't see your files. here's a full account of what we do see, what we don't, and how to verify it.",
    legal: { lastUpdated: LEGAL_LAST_UPDATED, sections: PRIVACY_SECTIONS },
  },
  {
    id: 'refunds',
    path: '/refunds',
    kind: 'legal',
    title: 'Refund Policy — anomia',
    description:
      'anomia refund policy. 30-day no-questions-asked refunds on Pro purchases. email contact@anomia.app to request.',
    heroH1: 'refund policy',
    heroLede: '30 days, no questions. email us and it gets done.',
    legal: { lastUpdated: LEGAL_LAST_UPDATED, sections: REFUND_SECTIONS },
  },
];

const blogPostRoutes: Route[] = BLOG_POSTS.map((post) => ({
  id: `blog-${post.slug}`,
  path: `/blog/${post.slug}`,
  kind: 'blog-post',
  title: post.metaTitle,
  description: post.description,
  heroH1: post.title,
  heroLede: post.lede,
  blogPost: post,
}));

export const ROUTES: Route[] = [
  ...APP_ROUTES,
  WHY_ROUTE,
  BLOG_INDEX_ROUTE,
  ...LEGAL_ROUTES,
  ...blogPostRoutes,
];

export const ROUTE_BY_PATH: ReadonlyMap<string, Route> = new Map(
  ROUTES.map((r) => [r.path, r] as const),
);

export const ROUTE_BY_ID: ReadonlyMap<RouteId, Route> = new Map(
  ROUTES.map((r) => [r.id, r] as const),
);

const home = ROUTE_BY_PATH.get('/');
if (!home) throw new Error('routes: home route missing');
export const HOME_ROUTE: Route = home;

/** JSON-LD payload for a route's structured data. App routes emit
 *  `SoftwareApplication`; blog posts emit `BlogPosting`. */
export const schemaForRoute = (route: Route): Record<string, unknown> => {
  const url = `${SITE_ORIGIN}${route.path === '/' ? '' : route.path}`;
  if (route.kind === 'blog-post' && route.blogPost) {
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: route.blogPost.title,
      description: route.description,
      url,
      datePublished: route.blogPost.publishedOn,
      author: { '@type': 'Organization', name: 'anomia' },
      publisher: { '@type': 'Organization', name: 'anomia' },
    };
  }
  if (route.kind === 'blog-index') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'anomia blog',
      description: route.description,
      url,
    };
  }
  if (route.kind === 'why') {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: route.heroH1,
      description: route.description,
      url,
      author: { '@type': 'Organization', name: 'anomia' },
      publisher: { '@type': 'Organization', name: 'anomia' },
    };
  }
  if (route.kind === 'legal') {
    return {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: route.title,
      description: route.description,
      url,
    };
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'anomia',
    applicationCategory: 'UtilityApplication',
    operatingSystem: 'Web browser',
    description: route.description,
    url,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
};
